/**
 * Simple Logging Framework for MSC Frontend
 * 
 * Features:
 * - Environment-specific log levels
 * - Structured logging with prefixes
 * - Performance-friendly (no-op in production)
 * - Type-safe
 */

export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3
}

export interface LoggerConfig {
  level: LogLevel;
  environment: string;
  enabledEnvironments: string[];
}

class Logger {
  private readonly config: LoggerConfig;

  constructor() {
    // Determine environment and log level based on build environment
    const environment = process.env.REACT_APP_ENVIRONMENT ?? 'development';
    
    this.config = {
      level: this.getLogLevelForEnvironment(environment),
      environment,
      enabledEnvironments: ['fat', 'fat-weu', 'fat-um', 'uat', 'test', 'development']
    };

    // Log framework initialization only in debug environments
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log(`[Logger] Initialized for environment: ${environment}, level: ${LogLevel[this.config.level]}`);
    }
  }

  private getLogLevelForEnvironment(env: string): LogLevel {
    switch (env.toLowerCase()) {
      case 'production':
      case 'prod':
        return LogLevel.ERROR; // Production: Only errors
      case 'uat':
      case 'test':
        return LogLevel.WARN;  // UAT/Test: Warnings and errors
      case 'fat':
      case 'fat-weu':
      case 'fat-um':
        return LogLevel.DEBUG; // FAT: All logs including debug
      case 'development':
      default:
        return LogLevel.DEBUG; // Development: All logs
    }
  }

  private shouldLog(level: LogLevel): boolean {
    const envCheck = this.config.environment.length > 0 && 
      this.config.enabledEnvironments.includes(this.config.environment.toLowerCase());
    return level <= this.config.level && envCheck;
  }

  private formatMessage(level: string, context: string, message: string, ...args: any[]): [string, ...any[]] {
    const timestamp = new Date().toISOString().substring(11, 23); // HH:mm:ss.SSS
    const formattedMessage = `${timestamp} [${level}] [${context}] ${message}`;
    return [formattedMessage, ...args];
  }

  error(context: string, message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(...this.formatMessage('ERROR', context, message, ...args));
    }
  }

  warn(context: string, message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(...this.formatMessage('WARN', context, message, ...args));
    }
  }

  info(context: string, message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.info(...this.formatMessage('INFO', context, message, ...args));
    }
  }

  debug(context: string, message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log(...this.formatMessage('DEBUG', context, message, ...args));
    }
  }

  // Convenience methods for common contexts
  auth = {
    debug: (message: string, ...args: any[]) => this.debug('Auth', message, ...args),
    info: (message: string, ...args: any[]) => this.info('Auth', message, ...args),
    warn: (message: string, ...args: any[]) => this.warn('Auth', message, ...args),
    error: (message: string, ...args: any[]) => this.error('Auth', message, ...args),
  };

  api = {
    debug: (message: string, ...args: any[]) => this.debug('API', message, ...args),
    info: (message: string, ...args: any[]) => this.info('API', message, ...args),
    warn: (message: string, ...args: any[]) => this.warn('API', message, ...args),
    error: (message: string, ...args: any[]) => this.error('API', message, ...args),
  };

  context = {
    debug: (message: string, ...args: any[]) => this.debug('Context', message, ...args),
    info: (message: string, ...args: any[]) => this.info('Context', message, ...args),
    warn: (message: string, ...args: any[]) => this.warn('Context', message, ...args),
    error: (message: string, ...args: any[]) => this.error('Context', message, ...args),
  };

  app = {
    debug: (message: string, ...args: any[]) => this.debug('App', message, ...args),
    info: (message: string, ...args: any[]) => this.info('App', message, ...args),
    warn: (message: string, ...args: any[]) => this.warn('App', message, ...args),
    error: (message: string, ...args: any[]) => this.error('App', message, ...args),
  };

  // Generic method for custom contexts
  log(level: LogLevel, context: string, message: string, ...args: any[]): void {
    switch (level) {
      case LogLevel.ERROR:
        this.error(context, message, ...args);
        break;
      case LogLevel.WARN:
        this.warn(context, message, ...args);
        break;
      case LogLevel.INFO:
        this.info(context, message, ...args);
        break;
      case LogLevel.DEBUG:
        this.debug(context, message, ...args);
        break;
    }
  }

  // Get current configuration (useful for debugging)
  getConfig(): LoggerConfig {
    return { ...this.config };
  }
}

// Export singleton instance
export const logger = new Logger();

// Export convenience function for direct usage
export default logger;
